import React from "react"
import { findOption, log } from "./utils"
import {
  AddressField,
  GenderField,
  HouseholdField,
  PhoneNumberField,
} from "./field_components/profile_fields"
import {
  CheckboxesField,
  DateField,
  DatePickerField,
  DropdownField,
  FileInputField,
  HeadingField,
  NumberField,
  TextAreaField,
  TextInputField,
  YesNoField,
} from "./field_components/basic_fields"
import {
  WorkflowCheckboxField,
  WorkflowCheckboxesField,
  WorkflowDropdownField,
} from "./field_components/workflow_fields"
import {
  genderOptions,
  renderSchoolGroupOptions,
} from "./field_components/field_components_options"

const componentForCustomFieldType = ({ field, included, ...props }) => {
  const { attributes, relationships, id } = field
  const { settings } = attributes
  const fieldDefinition = findOption({
    id: relationships.field_definitions.data.id,
    type: "FieldDefinition",
    included,
  })

  const {
    attributes: { data_type, config },
  } = fieldDefinition

  let field_type

  switch (data_type) {
    case "select":
      field_type = "custom_dropdown"
      break
    case "checkboxes":
      field_type = "custom_checkboxes"
      break
    case "boolean":
      field_type = "custom_boolean"
      break
    default:
      field_type = data_type
      break
  }

  return componentForFieldType(
    {
      attributes: {
        ...attributes,
        settings: { ...settings, ...config },
        field_type,
      },
      relationships,
      id,
      hide: false,
    },
    { ...props, included },
  )
}

export const componentForFieldType = (field, props = {}) => {
  const { attributes, relationships, id } = field
  const { field_type, settings } = attributes
  const {
    included,
    handleValueChange,
    handleMergedValueChange,
    handleAddAttachment,
    handleRemoveAttachment,
    maxTotalFileSize,
    totalFileSizeExceedsLimit,
    handleNumberValueChange,
    values,
    grades,
    phoneTypes,
    countries,
    addressTypes,
    errors,
  } = props

  const relevantErrors = errors.fieldErrors?.[id]

  switch (field_type) {
    case "string":
      return (
        <TextInputField
          id={id}
          key={id}
          errors={relevantErrors}
          attributes={attributes}
          onChange={handleValueChange}
          value={values[id]}
        />
      )
    case "checkboxes":
      return (
        <CheckboxesField
          key={id}
          id={id}
          errors={relevantErrors}
          attributes={attributes}
          onChange={handleValueChange}
          options={relationships.options.data.map((o) =>
            findOption({ ...o, included }),
          )}
          otherOption={settings && settings.other}
          value={values[id]}
        />
      )
    case "text":
    case "medical":
    case "note":
      return (
        <TextAreaField
          key={id}
          id={id}
          errors={relevantErrors}
          attributes={attributes}
          onChange={handleValueChange}
          value={values[id]}
        />
      )
    case "boolean":
      return (
        <YesNoField
          key={id}
          id={id}
          errors={relevantErrors}
          attributes={attributes}
          onChange={handleValueChange}
          value={values[id]}
        />
      )
    case "dropdown": {
      const options = relationships.options.data
        .map((o) => findOption({ ...o, included }))
        .sort((a, b) => a.attributes.sequence - b.attributes.sequence)
      return (
        <DropdownField
          key={id}
          id={id}
          errors={relevantErrors}
          attributes={attributes}
          onChange={handleValueChange}
          options={options.map(({ attributes: { label }, id }) => (
            <option key={id} value={id}>
              {label}
            </option>
          ))}
          otherOption={settings && settings.other}
          value={values[id]}
        />
      )
    }
    case "workflow_checkbox":
      return (
        <WorkflowCheckboxField
          key={id}
          id={id}
          errors={relevantErrors}
          attributes={attributes}
          onChange={handleValueChange}
          options={relationships.options.data.map((o) =>
            findOption({ ...o, included }),
          )}
          value={values[id]}
        />
      )
    case "workflow_checkboxes":
      return (
        <WorkflowCheckboxesField
          key={id}
          id={id}
          errors={relevantErrors}
          attributes={attributes}
          onChange={handleValueChange}
          onOtherChange={handleMergedValueChange}
          options={relationships.options.data.map((o) =>
            findOption({ ...o, included }),
          )}
          otherOption={relationships.fieldable}
          otherOptionWithNoWorkflow={settings.no_workflow_for_other_option}
          value={values[id]}
        />
      )
    case "workflow_dropdown":
      return (
        <WorkflowDropdownField
          key={id}
          id={id}
          errors={relevantErrors}
          attributes={attributes}
          onChange={handleValueChange}
          onOtherChange={handleMergedValueChange}
          options={relationships.options.data.map((o) =>
            findOption({ ...o, included }),
          )}
          otherOption={relationships.fieldable}
          otherOptionWithNoWorkflow={settings.no_workflow_for_other_option}
          value={values[id]}
        />
      )
    case "primary_campus":
      return (
        <DropdownField
          key={id}
          id={id}
          errors={relevantErrors}
          attributes={attributes}
          onChange={handleValueChange}
          options={relationships.campuses.data.map((o) => {
            const {
              attributes: { name },
              id,
            } = findOption({ ...o, included })
            return (
              <option key={id} value={id}>
                {name}
              </option>
            )
          })}
        />
      )
    case "marital_status":
      return (
        <DropdownField
          key={id}
          id={id}
          errors={relevantErrors}
          attributes={attributes}
          onChange={handleValueChange}
          options={relationships.marital_statuses.data.map((o) => {
            const {
              attributes: { label },
              id,
            } = findOption({ ...o, included })
            return (
              <option key={id} value={id}>
                {label}
              </option>
            )
          })}
        />
      )
    case "school":
      return (
        <DropdownField
          key={id}
          id={id}
          errors={relevantErrors}
          attributes={attributes}
          onChange={handleValueChange}
          options={renderSchoolGroupOptions(
            relationships.school_options,
            included,
          )}
        />
      )
    case "grade":
      return (
        <DropdownField
          key={id}
          id={id}
          errors={relevantErrors}
          attributes={attributes}
          onChange={handleValueChange}
          options={grades.map(({ key, value }) => {
            return (
              <option key={key} value={key}>
                {value}
              </option>
            )
          })}
        />
      )
    case "gender":
      return (
        <GenderField
          key={id}
          id={id}
          errors={relevantErrors}
          attributes={attributes}
          onChange={handleValueChange}
          options={genderOptions(relationships.genders, included)}
          value={values[id]}
        />
      )
    case "heading":
      return <HeadingField key={id} id={id} attributes={attributes} />
    case "custom_field":
      return componentForCustomFieldType({ field, included, ...props })
    case "custom_boolean":
      return (
        <DropdownField
          key={id}
          id={id}
          errors={relevantErrors}
          attributes={attributes}
          onChange={handleValueChange}
          options={[
            <option key="yes" value={true}>
              Yes
            </option>,
            <option key="no" value={false}>
              No
            </option>,
          ]}
        />
      )
    case "custom_checkboxes":
      return (
        <CheckboxesField
          key={id}
          id={id}
          errors={relevantErrors}
          attributes={attributes}
          onChange={handleValueChange}
          options={relationships.field_options.data.map((o) =>
            findOption({ ...o, included }),
          )}
        />
      )
    case "custom_dropdown":
      return (
        <DropdownField
          key={id}
          id={id}
          errors={relevantErrors}
          attributes={attributes}
          onChange={handleValueChange}
          options={relationships.field_options.data.map((o) => {
            const {
              attributes: { label },
              id,
            } = findOption({ ...o, included })
            return (
              <option key={id} value={id}>
                {label}
              </option>
            )
          })}
        />
      )
    case "phone_number":
      return (
        <PhoneNumberField
          key={id}
          id={id}
          errors={relevantErrors}
          attributes={attributes}
          onChange={handleMergedValueChange}
          phoneTypes={phoneTypes}
          value={values[id]}
        />
      )
    case "birthday":
      return (
        <DateField
          key={id}
          id={id}
          errors={relevantErrors}
          attributes={attributes}
          onChange={handleValueChange}
          className="date-field__input"
          value={values[id]}
        />
      )
    case "file": {
      const fileUploadErrors = relevantErrors
        ? { attachments: relevantErrors.value }
        : {}
      const multipleFiles = !!attributes.settings["multiple_files"]

      return (
        <FileInputField
          key={id}
          id={id}
          attributes={attributes}
          errors={fileUploadErrors}
          props={props}
          onAddFile={handleAddAttachment}
          onRemoveFile={handleRemoveAttachment}
          multiFileInput={multipleFiles}
          maxTotalFileSize={maxTotalFileSize}
          totalFileSizeExceedsLimit={totalFileSizeExceedsLimit()}
        />
      )
    }
    case "date":
      return (
        <DatePickerField
          key={id}
          id={id}
          errors={relevantErrors}
          attributes={attributes}
          onChange={handleValueChange}
          className="date-field__input"
          value={values[id]}
        />
      )
    case "anniversary":
      return (
        <DateField
          key={id}
          id={id}
          errors={relevantErrors}
          attributes={attributes}
          onChange={handleValueChange}
          className="date-field__input"
          value={values[id]}
        />
      )
    case "address":
      return (
        <AddressField
          key={id}
          id={id}
          errors={relevantErrors}
          attributes={attributes}
          value={values[id]}
          countries={countries}
          locationOptions={addressTypes.map((at) => (
            <option key={at}>{at}</option>
          ))}
          onChange={handleMergedValueChange}
        />
      )
    case "number":
      return (
        <NumberField
          key={id}
          id={id}
          errors={relevantErrors}
          attributes={attributes}
          onChange={(value) => handleNumberValueChange(value, id)}
          value={values[id] ?? ""}
        />
      )
    case "household":
      return (
        <HouseholdField
          key={id}
          id={id}
          values={values[id]}
          errors={relevantErrors}
          onChange={handleValueChange}
          attributes={attributes}
          phoneTypes={phoneTypes}
          genderOptions={genderOptions(relationships.genders, included)}
          maritalStatusOptions={relationships.marital_statuses.data.map((o) => {
            const {
              attributes: { label },
              id,
            } = findOption({ ...o, included })
            return (
              <option key={id} value={id}>
                {label}
              </option>
            )
          })}
          grades={grades}
          schoolOptions={renderSchoolGroupOptions(
            relationships.school_options,
            included,
          )}
        />
      )
    default:
      log.error("field_type isn't handled:", field_type)
      return null
  }
}
